import { useState } from 'react';
import { makeStyles, tokens, Text, Spinner, mergeClasses } from '@coreai/fluentui-react';
import { ChevronRight20Regular, ChevronDown20Regular, Flash16Filled } from '@fluentui/react-icons';
import { RunStatusBadge } from './RunStatusBadge';
import { RunStatusIcon } from './RunStatusIcon';
import { ExecutionItem } from './ExecutionItem';
import type { Run } from '@/types/monitoring';
import { useTelemetry } from '@/hooks/useTelemetry';
import { formatDateTime, formatDuration } from '@/utils/dateTime';

type RunItemProps = {
  run: Run;
  isSelected: boolean;
  selectedExecutionId: string | null;
  onSelectRun: (runId: string) => void;
  onSelectExecution: (executionId: string) => void;
  isExpanded?: boolean;
  onToggleExpand?: (runId: string) => void;
  /** If true, shows workflow name. If false (default), shows date as primary text */
  showWorkflowName?: boolean;
};

const useStyles = makeStyles({
  container: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
  },
  rowSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Selected,
    },
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  chevronHidden: {
    visibility: 'hidden',
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  primaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  workflowName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateTime: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  details: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  statusContainer: {
    flexShrink: 0,
  },
  statusIcon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  executionsContainer: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalM,
  },
  draftIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    marginLeft: tokens.spacingHorizontalS,
  },
  draftIcon: {
    fontSize: tokens.fontSizeBase300,
  },
});

export const RunItem = ({
  run,
  isSelected,
  selectedExecutionId,
  onSelectRun,
  onSelectExecution,
  isExpanded: controlledExpanded,
  onToggleExpand,
  showWorkflowName = false,
}: RunItemProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpanded = controlledExpanded ?? internalExpanded;
  const canExpand = run.isAgentic && run.executionCount > 0;

  const handleRowClick = () => {
    trackEvent('execution.run.clicked', { runId: run.id, status: run.status });
    onSelectRun(run.id);
    if (canExpand) {
      if (onToggleExpand) {
        onToggleExpand(run.id);
      } else {
        setInternalExpanded(!internalExpanded);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowClick();
    }
  };

  return (
    <div className={styles.container}>
      <button
        className={mergeClasses(styles.row, isSelected && styles.rowSelected)}
        onClick={handleRowClick}
        onKeyDown={handleKeyDown}
        aria-expanded={canExpand ? isExpanded : undefined}
        aria-selected={isSelected}
      >
        <span className={mergeClasses(styles.chevron, !canExpand && styles.chevronHidden)}>
          {isExpanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
        </span>
        <div className={styles.content}>
          {showWorkflowName ? (
            <>
              <Text className={styles.workflowName}>{run.workflowName}</Text>
              <Text className={styles.details}>
                {formatDateTime(run.startTime, 'adaptive', { fallback: '' })} · {formatDuration(run.duration, { style: 'execution', fallback: 'In progress' })}
                {run.isAgentic && ` · ${run.executionCount} execution${run.executionCount !== 1 ? 's' : ''}`}
              </Text>
            </>
          ) : (
            <>
              <div className={styles.primaryRow}>
                <span className={styles.statusIcon}>
                  <RunStatusIcon status={run.status} size="small" />
                </span>
                <Text className={styles.dateTime}>{formatDateTime(run.startTime, 'adaptive', { fallback: '' })}</Text>
                {run.isDraftRun && (
                  <span className={styles.draftIndicator}>
                    <Flash16Filled className={styles.draftIcon} />
                    Draft
                  </span>
                )}
              </div>
              <Text className={styles.details}>
                {formatDuration(run.duration, { style: 'execution', fallback: 'In progress' })}
                {run.isAgentic && ` · ${run.executionCount} execution${run.executionCount !== 1 ? 's' : ''}`}
              </Text>
            </>
          )}
        </div>
        {showWorkflowName && (
          <div className={styles.statusContainer}>
            <RunStatusBadge status={run.status} size="small" />
          </div>
        )}
      </button>

      {isExpanded && canExpand && (
        <div className={styles.executionsContainer}>
          {run.executions ? (
            run.executions.map((execution) => (
              <ExecutionItem
                key={execution.id}
                execution={execution}
                isSelected={execution.id === selectedExecutionId}
                onSelect={onSelectExecution}
              />
            ))
          ) : (
            <div className={styles.loadingContainer}>
              <Spinner size="tiny" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
